import { useQuery } from '@tanstack/react-query';
import { Radio } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/Table';
import { PageLoader } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch } from '../lib/apiFetch';
import { formatDateTime } from '../lib/format';
import { useT } from '../i18n';

interface Channel {
  id: number;
  chat_id: number;
  title: string;
  username: string | null;
  is_active: boolean;
  created_at: string;
}

export function Channels() {
  const t = useT();
  const list = useQuery({
    queryKey: ['channels'],
    queryFn: () => apiFetch<{ channels: Channel[] }>('/api/admin/channels'),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('channels.registerTitle')}</CardTitle>
          <CardDescription>
            {t('channels.registerIntro')}
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-600">
              <li>{t('channels.step1')}</li>
              <li>{t('channels.step2')}</li>
              <li>{t('channels.step3')}</li>
              <li>{t('channels.step4')}</li>
              <li>{t('channels.step5')}</li>
            </ol>
          </CardDescription>
        </CardHeader>
      </Card>

      {list.isLoading ? (
        <PageLoader />
      ) : !list.data || list.data.channels.length === 0 ? (
        <EmptyState
          icon={<Radio className="h-10 w-10" />}
          title={t('channels.emptyTitle')}
          description={t('channels.emptyDescription')}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>{t('common.title')}</TH>
                  <TH>chat_id</TH>
                  <TH>@username</TH>
                  <TH>{t('common.status')}</TH>
                  <TH>{t('channels.registeredAt')}</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.channels.map((c) => (
                  <TR key={c.id}>
                    <TD className="font-medium">{c.title}</TD>
                    <TD className="font-mono text-xs text-slate-600">{c.chat_id}</TD>
                    <TD>{c.username ? `@${c.username}` : '—'}</TD>
                    <TD>
                      <Badge variant={c.is_active ? 'success' : 'muted'}>
                        {c.is_active ? t('common.active') : t('common.archived')}
                      </Badge>
                    </TD>
                    <TD className="text-sm text-slate-500">{formatDateTime(c.created_at)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
